import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import { add_weight_api, update_weight_api } from "src/DAL/exercise/exercise";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrTextField from "src/design-system/components/GrTextField";
import GrButton from "src/design-system/components/GrButton";
import GrIcon from "src/design-system/components/GrIcon";

// Check-in form (inside the Weight Tracker dialog): date, time, weight, body fat, optional
// measurements and a note.

const select_array = [
  {
    name: "chest",
    label: "Chest",
    require: false,
  },
  {
    name: "waist",
    label: "Waist",
    require: false,
  },
  {
    name: "hip",
    label: "Hip",
    require: false,
  },
  {
    name: "arm",
    label: "Arm",
    require: false,
  },
  {
    name: "thigh",
    label: "Thigh",
    require: false,
  },
];

const twoColSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
  gap: gr.space[4],
};
const threeColSx = {
  display: "grid",
  gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))" },
  gap: gr.space[4],
};
const unit = (text) => (text ? { endAdornment: <InputAdornment position="end">{text}</InputAdornment> } : undefined);

const AddWeight = ({ setOpenWeight, weight_list, editValues, userPre }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [isAddMer, setIsAddMer] = useState(false);

  const now = new Date();
  const currentDate = now.toISOString().split("T")[0];
  const currentTime = now.toTimeString().slice(0, 5);

  const [inputs, setInputs] = useState({
    current_weight: "",
    body_fat: "",
    chest: "",
    waist: "",
    hip: "",
    arm: "",
    thigh: "",
    note: "",
    date: currentDate,
    time: currentTime,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!inputs.body_fat && !inputs.current_weight) {
      enqueueSnackbar(
        "Please enter either your Current Weight or Body Fat, or both if available.",
        { variant: "error" },
      );
      return;
    }

    setIsFormLoading(true);
    let postData = {
      current_weight: inputs.current_weight || 0,
      body_fat: inputs.body_fat || 0,
      chest: inputs.chest,
      waist: inputs.waist,
      hip: inputs.hip,
      arm: inputs.arm,
      thigh: inputs.thigh,
      note: inputs.note,
      date: inputs.date,
      time: inputs.time,
    };

    if (!isAddMer) {
      postData.chest = "";
      postData.waist = "";
      postData.hip = "";
      postData.arm = "";
      postData.thigh = "";
    }

    const result = editValues
      ? await update_weight_api(postData, editValues?._id)
      : await add_weight_api(postData);
    if (result.code === 200) {
      enqueueSnackbar(result.message, { variant: "success" });
      setIsFormLoading(false);
      setOpenWeight(false);
      weight_list();
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsFormLoading(false);
    }
  };

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  useEffect(() => {
    if (editValues) {
      const is_show =
        editValues?.arm ||
        editValues?.hip ||
        editValues?.thigh ||
        editValues?.waist ||
        editValues?.chest;
      setIsAddMer(is_show);
      setInputs((prevInputs) => ({
        ...prevInputs,
        ...editValues,
        date: editValues?.date
          ? editValues?.date.split("T")[0]
          : prevInputs.date,
      }));
    }
  }, [editValues]);

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: gr.space[4], pt: gr.space[2] }}>
      <Box sx={twoColSx}>
        <GrTextField
          type="date"
          label="Date"
          name="date"
          value={inputs.date}
          required
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
        <GrTextField
          type="time"
          label="Time"
          name="time"
          value={inputs.time}
          required
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
        <GrTextField
          type="number"
          label="Weight"
          name="current_weight"
          value={inputs.current_weight}
          onChange={handleChange}
          InputProps={unit(userPre?.weight_unit)}
        />
        <GrTextField
          type="number"
          label="Body fat"
          name="body_fat"
          value={inputs.body_fat}
          onChange={handleChange}
          InputProps={unit("%")}
        />
      </Box>

      <Box>
        <GrButton
          variant="link"
          size="sm"
          startIcon={<GrIcon icon={isAddMer ? icons.minus : icons.plus} size="sm" />}
          onClick={() => {
            setIsAddMer(!isAddMer);
          }}
        >
          {isAddMer ? "Remove measurements" : "Add measurements"}
        </GrButton>
      </Box>

      {isAddMer && (
        <Box sx={threeColSx}>
          {select_array.map((item) => (
            <GrTextField
              key={item.name}
              type="number"
              required={item.require}
              name={item.name}
              value={inputs[item.name]}
              onChange={handleChange}
              label={item.label}
              InputProps={unit(userPre?.[`${item.name}_unit`])}
            />
          ))}
        </Box>
      )}

      <GrTextField
        label="Note"
        placeholder="How did this week go?"
        multiline
        minRows={3}
        name="note"
        value={inputs.note}
        onChange={handleChange}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: gr.space[3] }}>
        <GrButton variant="quiet" onClick={() => setOpenWeight(false)}>
          Cancel
        </GrButton>
        <GrButton type="submit" loading={isFormLoading}>
          {editValues ? "Save changes" : "Save check-in"}
        </GrButton>
      </Box>
    </Box>
  );
};

export default AddWeight;
